import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { cn } from '@/lib/utils';
import { isPracticalType } from '../../utils/practicalResults';

interface OutcomeItem {
  type: string;
  is_practical?: boolean;
}

interface OutcomeTypesChartProps {
  outcomes: OutcomeItem[];
}

interface TypeBucket {
  key: string;
  type: string;
  count: number;
  practical: boolean;
  fill: string;
}

// Practical → primary-blue family (CTU blue), darker → lighter as count rank grows.
const PRACTICAL_SHADES = [
  'hsl(var(--primary))',
  'hsl(204 70% 65%)',
  'hsl(204 70% 78%)',
  'hsl(204 70% 88%)',
];

// Academic → warm slate family, anchored on ACCENT_ACADEMIC (#5F6B7E ≈ hsl 217 15% 43%).
// Top-rank uses the canonical academic hue; siblings step lighter.
const ACADEMIC_SHADES = [
  'hsl(217 15% 43%)',
  'hsl(217 15% 55%)',
  'hsl(217 15% 67%)',
  'hsl(217 15% 78%)',
  'hsl(217 15% 87%)',
];

function shade(rank: number, palette: string[]): string {
  return palette[Math.min(rank, palette.length - 1)];
}

export default function OutcomeTypesChart({ outcomes }: OutcomeTypesChartProps) {
  const { t } = useTranslation('person');
  const [show, setShow] = useState(false);

  const data = useMemo<TypeBucket[]>(() => {
    if (outcomes.length === 0) return [];
    const counts = new Map<string, { count: number; practical: boolean }>();
    for (const o of outcomes) {
      const key = (o.type || 'Unknown').trim() || 'Unknown';
      const practical = Boolean(o.is_practical) || isPracticalType(o.type || '');
      const existing = counts.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        counts.set(key, { count: 1, practical });
      }
    }

    const all = Array.from(counts.entries())
      .map(([type, { count, practical }]) => ({ type, count, practical }))
      .sort((a, b) => b.count - a.count);

    let practicalRank = 0;
    let academicRank = 0;
    return all.map<TypeBucket>(({ type, count, practical }) => {
      const fill = practical
        ? shade(practicalRank++, PRACTICAL_SHADES)
        : shade(academicRank++, ACADEMIC_SHADES);
      return {
        key: `${practical ? 'p' : 'a'}-${type}`,
        type,
        count,
        practical,
        fill,
      };
    });
  }, [outcomes]);

  const total = useMemo(() => data.reduce((sum, d) => sum + d.count, 0), [data]);

  const chartConfig = useMemo<ChartConfig>(() => {
    const cfg: ChartConfig = {
      count: { label: t('table.type') },
    };
    for (const d of data) {
      cfg[d.key] = { label: d.type, color: d.fill };
    }
    return cfg;
  }, [data, t]);

  if (outcomes.length === 0) return null;

  return (
    <Card className="mb-6 border-ctu-border shadow-none">
      <CardHeader className="flex-row items-center justify-between space-y-0 px-6 py-5">
        <CardTitle className="flex items-baseline gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
            {t('sections.outcomeTypes')}
          </span>
          <span className="text-[11px] font-mono tabular-nums text-ctu-text-muted">
            {data.length}
          </span>
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={() => setShow(s => !s)}>
          {show ? t('buttons.hideOutcomeTypes') : t('buttons.showOutcomeTypes')}
          <span
            className={cn(
              'inline-block transition-transform text-ctu-text-muted ml-2',
              show && 'rotate-180',
            )}
            aria-hidden
          >
            ▼
          </span>
        </Button>
      </CardHeader>

      {show && (
        <CardContent className="px-6 pt-2 pb-6">
        <div className="mb-4 flex items-baseline gap-3 flex-wrap">
          <span className="text-xs text-ctu-text-muted">
            {t('sections.outcomeTypesSubtitle')}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-[260px_1fr] md:items-center">
          <ChartContainer
            config={chartConfig}
            className="aspect-square w-full max-w-[260px] mx-auto"
          >
            <PieChart>
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    nameKey="type"
                    hideLabel
                    formatter={(value: any, _name: any, item: any) => {
                      const pct = total ? Math.round((Number(value) / total) * 100) : 0;
                      return (
                        <div className="flex w-full items-center justify-between gap-3">
                          <span className="flex items-center gap-2">
                            <span
                              className="size-2 shrink-0 rounded-[2px]"
                              style={{ backgroundColor: item?.payload?.fill }}
                            />
                            <span>{item?.payload?.type}</span>
                          </span>
                          <span className="font-mono tabular-nums text-foreground">
                            {value} <span className="text-ctu-text-muted">({pct}%)</span>
                          </span>
                        </div>
                      );
                    }}
                  />
                }
              />
              <Pie
                data={data}
                dataKey="count"
                nameKey="type"
                innerRadius="55%"
                outerRadius="92%"
                paddingAngle={1.5}
                strokeWidth={2}
                stroke="hsl(var(--card))"
              >
                {data.map(d => (
                  <Cell key={d.key} fill={d.fill} />
                ))}
              </Pie>
            </PieChart>
          </ChartContainer>

          <ul className="m-0 flex flex-col divide-y divide-border list-none p-0">
            {data.map(d => {
              const pct = total ? Math.round((d.count / total) * 100) : 0;
              return (
                <li
                  key={d.key}
                  className="flex items-center gap-3 py-2 text-sm"
                >
                  <span
                    className="size-2.5 shrink-0 rounded-[3px]"
                    style={{ backgroundColor: d.fill }}
                    aria-hidden
                  />
                  <span className="flex-1 min-w-0 truncate text-ctu-text">{d.type}</span>
                  <span className="font-mono tabular-nums text-ctu-text-muted text-xs">
                    {d.count} ({pct}%)
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
        </CardContent>
      )}
    </Card>
  );
}
